<template>
  <div class="page">
    <h2>理疗师管理</h2>
    <div class="toolbar">
      <input v-model="filters.keyword" placeholder="按姓名搜索" />
      <select v-model="filters.status">
        <option value="">全部状态</option>
        <option value="ACTIVE">可用</option>
        <option value="INACTIVE">停用</option>
      </select>
      <select v-model="filters.teamId">
        <option value="">全部团队</option>
        <option v-for="t in teamOptions" :key="t.id" :value="String(t.id)">{{ t.name }}</option>
      </select>
      <button :disabled="loading" @click="loadData">查询</button>
      <button :disabled="loading" @click="openTeamDialog">团队管理</button>
      <button :disabled="loading" @click="openCreate">新增理疗师</button>
    </div>

    <table class="therapist-table">
      <colgroup>
        <col style="width: 60px;">
        <col style="width: 100px;">
        <col style="width: 60px;">
        <col style="width: 100px;">
        <col>
        <col style="width: 80px;">
        <col style="width: 180px;">
      </colgroup>
      <thead>
        <tr><th>ID</th><th>姓名</th><th>性别</th><th>团队</th><th>项目与等级</th><th>状态</th><th>操作</th></tr>
      </thead>
      <tbody>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ item.name }}</td>
          <td>{{ genderLabel(item.gender) }}</td>
          <td>{{ item.team?.name || '-' }}</td>
          <td class="activities-cell">
            <template v-if="item.activities && item.activities.length > 0">
              <div class="activities-wrap">
                <span v-for="act in item.activities" :key="act.activityId" class="activity-tag">
                  {{ act.activityName }}<template v-if="act.grade">（{{ act.grade }}）</template>
                </span>
              </div>
            </template>
            <span v-else>-</span>
          </td>
          <td>
            <span :class="['status-badge', 'status-' + item.status.toLowerCase()]">
              {{ statusMap[item.status] || item.status }}
            </span>
          </td>
          <td>
            <button :disabled="loading" @click="openEdit(item)">编辑</button>
            <button :disabled="loading" @click="toggleStatus(item)">{{ item.status === 'ACTIVE' ? '停用' : '启用' }}</button>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button :disabled="loading || page===0" @click="changePage(page-1)">上一页</button>
      <span>{{ page + 1 }}</span>
      <button :disabled="loading || page+1>=totalPages" @click="changePage(page+1)">下一页</button>
    </div>

    <div v-if="showDialog" class="dialog-mask">
      <div class="dialog">
        <h3>{{ editingId ? '编辑理疗师' : '新增理疗师' }}</h3>
        <input v-model="form.name" placeholder="姓名" />
        <div class="form-group">
          <label class="full-width">
            <span>性别</span>
            <select v-model="form.gender" class="team-select" required>
              <option disabled value="">请选择</option>
              <option value="MALE">男</option>
              <option value="FEMALE">女</option>
              <option value="UNKNOWN">未知</option>
            </select>
          </label>
        </div>
        <div class="form-group">
          <label class="full-width">
            <span class="field-label">头像</span>
            <span class="field-hint block-hint">推荐 <strong>400×400</strong> 像素（比例 1:1 正方形）。用户端以圆形头像展示，请上传正面清晰人像。支持 JPG / PNG / WebP，单张建议不超过 <strong>100KB</strong> 以节省存储空间。</span>
            <div class="image-upload-wrapper">
              <input
                ref="avatarInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="uploadAvatar"
              />
              <div class="avatar-preview-container">
                <img
                  v-if="form.avatarUrl"
                  :src="getPreviewUrl(form.avatarUrl)"
                  alt="头像预览"
                  class="avatar-preview"
                />
                <div v-else class="image-placeholder">暂无头像</div>
              </div>
              <div class="image-upload-actions">
                <button type="button" class="ghost-button" @click="$refs.avatarInput.click()">
                  {{ form.avatarUrl ? '更换图片' : '选择图片' }}
                </button>
                <button
                  v-if="form.avatarUrl"
                  type="button"
                  class="danger-link"
                  @click.prevent="form.avatarUrl = ''"
                >
                  清除
                </button>
                <span v-if="loadingField === 'avatarUrl'" class="upload-status">上传中...</span>
              </div>
            </div>
          </label>
        </div>
        <textarea v-model="form.bio" placeholder="简介" class="bio-textarea"></textarea>
        <div class="form-group">
          <label class="full-width">
            <span>所属团队</span>
            <select v-model="form.teamId" class="team-select">
              <option :value="null">无团队</option>
              <option v-for="t in teamOptions" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </label>
        </div>
        <div class="form-group">
          <label class="full-width">
            <span class="field-label">关联项目与等级</span>
            <span class="field-hint" style="font-size: 12px; color: #888;">勾选项目后，若该项目预设了等级选项则从下拉选择，否则可手填</span>
          </label>
          <div class="activity-checklist">
            <div v-for="act in activityOptions" :key="act.id" class="activity-check-row">
              <label class="activity-check-label">
                <input type="checkbox" :value="act.id" v-model="selectedActivityIds" @change="onActivityToggle(act)" />
                <span>{{ act.name }}</span>
              </label>
              <template v-if="selectedActivityIds.includes(act.id)">
                <select v-if="getGradeOptions(act).length > 0" v-model="getGradeInput(act.id).grade" class="grade-select">
                  <option value="">不选等级</option>
                  <option v-for="opt in getGradeOptions(act)" :key="opt" :value="opt">{{ opt }}</option>
                </select>
                <input v-else v-model="getGradeInput(act.id).grade" placeholder="等级" class="grade-input" />
              </template>
            </div>
            <div v-if="activityOptions.length === 0" class="no-activities">暂无可选项目</div>
          </div>
        </div>
        <div class="form-group">
          <label class="full-width">
            <span class="field-label">任务提醒微信</span>
            <span class="field-hint block-hint">理疗师需先用微信登录康养 H5。输入其手机号搜索并绑定，支付成功后将收到服务号任务提醒。</span>
            <div class="notify-wechat-row">
              <input v-model="notifyPhone" type="tel" maxlength="11" placeholder="手机号" class="notify-phone-input" />
              <button type="button" class="ghost-button" :disabled="loading || !notifyPhone" @click="searchNotifyCandidates">搜索</button>
            </div>
            <select v-model="form.notifyWechatUserId" class="team-select">
              <option :value="null">不绑定</option>
              <option v-for="c in notifyCandidates" :key="c.id" :value="c.id">
                {{ c.nickname || '微信用户' }}（{{ c.openidHint }}）{{ c.notifiable ? '' : '· 暂不可推送' }}
              </option>
            </select>
            <p v-if="notifySearchDone && notifyCandidates.length === 0" class="field-hint">未找到该手机号关联的微信账号，请确认已在康养 H5 登录且理疗客户档案已关联微信</p>
            <p v-else-if="notifySearchDone && notifyCandidates.every(c => !c.notifiable)" class="field-hint warn-hint">已找到账号，但暂无法接收服务号消息。请让理疗师用微信重新登录一次康养 H5 后再绑定。</p>
          </label>
        </div>
        <div class="actions">
          <button :disabled="loading" @click="showDialog=false">取消</button>
          <button :disabled="loading" @click="save">保存</button>
        </div>
      </div>
    </div>

    <div v-if="showTeamDialog" class="dialog-mask">
      <div class="dialog dialog-wide">
        <h3>团队管理</h3>
        <div class="category-toolbar">
          <button :disabled="loading" @click="openCreateTeam">新增团队</button>
        </div>
        <table class="category-table">
          <thead>
            <tr><th>ID</th><th>团队名称</th><th>排序</th><th>状态</th><th>操作</th></tr>
          </thead>
          <tbody>
            <tr v-for="team in teamList" :key="team.id">
              <td>{{ team.id }}</td>
              <td>{{ team.name }}</td>
              <td>{{ team.sortOrder }}</td>
              <td><span :class="['status-badge', 'status-' + team.status.toLowerCase()]">{{ team.status === 'ACTIVE' ? '启用' : '停用' }}</span></td>
              <td>
                <button :disabled="loading" @click="openEditTeam(team)">编辑</button>
                <button :disabled="loading" @click="toggleTeamStatus(team)">{{ team.status === 'ACTIVE' ? '禁用' : '启用' }}</button>
              </td>
            </tr>
            <tr v-if="!teamList.length"><td colspan="5" style="text-align:center;color:#999">暂无团队</td></tr>
          </tbody>
        </table>

        <div v-if="showTeamEditDialog" class="dialog-mask">
          <div class="dialog">
            <h3>{{ editingTeamId ? '编辑团队' : '新增团队' }}</h3>
            <label class="field-block">团队名称
              <input v-model="teamForm.name" placeholder="例如：康复团队" />
            </label>
            <label class="field-block">排序（越小越靠前）
              <input v-model.number="teamForm.sortOrder" type="number" placeholder="0" />
            </label>
            <div class="actions">
              <button :disabled="loading" @click="showTeamEditDialog=false">取消</button>
              <button :disabled="loading" @click="saveTeam">保存</button>
            </div>
          </div>
        </div>

        <div class="actions">
          <button @click="showTeamDialog=false">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="showDeactivateModal" class="dialog-mask" @click="closeDeactivateModal">
      <div class="dialog deactivate-dialog" @click.stop>
        <h3>{{ deactivateModalMode === 'blocked' ? '无法停用' : '确认停用理疗师' }}</h3>
        <div v-if="deactivateTarget" class="deactivate-summary">
          <p><strong>理疗师：</strong>{{ deactivateTarget.item.name }}</p>
        </div>
        <div v-if="deactivateModalMode === 'blocked'" class="deactivate-alert deactivate-alert-danger">
          <p><strong>该理疗师仍有 {{ deactivateCheck?.activeBookingCount || 0 }} 笔未完成预约，无法停用。</strong></p>
          <p v-if="deactivateCheck?.statusBreakdown && Object.keys(deactivateCheck.statusBreakdown).length">
            预约分布：{{ formatBookingStatusBreakdown(deactivateCheck.statusBreakdown) }}
          </p>
          <p class="deactivate-hint">请先处理完相关预约（完成、取消或改期）后再尝试停用。</p>
        </div>
        <div v-else class="deactivate-alert deactivate-alert-warning">
          <p><strong>请慎重停用！</strong></p>
          <p>停用后 C 端将不再展示该理疗师，用户无法选择其发起新预约，但历史预约数据仍会保留。</p>
          <p>当前暂无未完成预约，确认要继续停用吗？</p>
        </div>
        <div class="actions">
          <button type="button" class="dialog-cancel-btn" @click="closeDeactivateModal">
            {{ deactivateModalMode === 'blocked' ? '知道了' : '取消' }}
          </button>
          <button
            v-if="deactivateModalMode === 'confirm'"
            type="button"
            class="deactivate-confirm-btn"
            :disabled="loading"
            @click="confirmDeactivate"
          >
            {{ loading ? '处理中...' : '确认停用' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { therapistService } from '@/services/therapistService'
import { therapyTeamService } from '@/services/therapyTeamService'
import { therapyActivityService } from '@/services/therapyActivityService'
import { normalizeOssUrlForPresign } from '@/utils/ossUrl'

export default {
  name: 'TherapistManagement',
  data() {
    return {
      list: [],
      form: { name: '', gender: '', avatarUrl: '', bio: '', status: 'ACTIVE', teamId: null, notifyWechatUserId: null },
      notifyPhone: '',
      notifyCandidates: [],
      notifySearchDone: false,
      selectedActivityIds: [],
      therapistActivities: [],
      filters: { keyword: '', status: '', teamId: '' },
      statusMap: { ACTIVE: '可用', INACTIVE: '停用' },
      activityOptions: [],
      teamOptions: [],
      teamList: [],
      showTeamDialog: false,
      showTeamEditDialog: false,
      editingTeamId: null,
      teamForm: { name: '', sortOrder: 0, status: 'ACTIVE' },
      page: 0,
      size: 10,
      totalPages: 1,
      showDialog: false,
      editingId: null,
      loading: false,
      loadingField: '',
      previewUrls: {},
      showDeactivateModal: false,
      deactivateModalMode: 'confirm',
      deactivateTarget: null,
      deactivateCheck: null,
      bookingStatusLabels: {
        PENDING_PAYMENT: '待付款',
        PAID: '已付款',
        CONFIRMED: '已确认',
        CHECKED_IN: '已到店'
      }
    }
  },
  mounted() {
    this.loadData()
    this.loadActivities()
    this.loadTeams()
  },
  methods: {
    genderLabel(g) {
      if (g === 'MALE') return '男'
      if (g === 'FEMALE') return '女'
      if (g === 'UNKNOWN') return '未知'
      return '—'
    },
    async loadData() {
      this.loading = true
      try {
        const res = await therapistService.fetchList({
          keyword: this.filters.keyword,
          status: this.filters.status,
          teamId: this.filters.teamId || undefined,
          page: this.page,
          size: this.size
        })
        this.list = res.data?.content || []
        this.totalPages = res.data?.totalPages || 1
      } catch (e) {
        alert(e?.response?.data?.error || '加载理疗师失败')
      } finally {
        this.loading = false
      }
    },
    async loadActivities() {
      try {
        const res = await therapyActivityService.fetchList({ status: 'PUBLISHED', page: 0, size: 200 })
        this.activityOptions = res.data?.content || []
      } catch (e) {
        console.error('加载活动列表失败', e)
      }
    },
    getGradeOptions(act) {
      if (act.gradeOptions && act.gradeOptions.length > 0) return act.gradeOptions
      return []
    },
    getGradeInput(activityId) {
      let found = this.therapistActivities.find(a => a.activityId === activityId)
      if (!found) {
        found = { activityId, grade: '' }
        this.therapistActivities.push(found)
      }
      return found
    },
    onActivityToggle(act) {
      const id = act.id
      if (this.selectedActivityIds.includes(id)) {
        this.getGradeInput(id)
      } else {
        this.therapistActivities = this.therapistActivities.filter(a => a.activityId !== id)
      }
    },
    async loadTeams() {
      try {
        const res = await therapyTeamService.fetchList()
        this.teamOptions = (res.data || []).filter(t => t.status === 'ACTIVE')
      } catch (e) {
        console.error('加载团队失败', e)
      }
    },
    async openTeamDialog() {
      this.showTeamDialog = true
      await this.loadTeamList()
    },
    async loadTeamList() {
      try {
        const res = await therapyTeamService.fetchList()
        this.teamList = res.data || []
      } catch (e) {
        alert(e?.response?.data?.error || '加载团队列表失败')
      }
    },
    openCreateTeam() {
      this.editingTeamId = null
      this.teamForm = { name: '', sortOrder: 0, status: 'ACTIVE' }
      this.showTeamEditDialog = true
    },
    openEditTeam(team) {
      this.editingTeamId = team.id
      this.teamForm = { name: team.name, sortOrder: team.sortOrder, status: team.status }
      this.showTeamEditDialog = true
    },
    async saveTeam() {
      if (!this.teamForm.name.trim()) { alert('请输入团队名称'); return }
      this.loading = true
      try {
        if (this.editingTeamId) await therapyTeamService.update(this.editingTeamId, this.teamForm)
        else await therapyTeamService.create(this.teamForm)
        this.showTeamEditDialog = false
        await this.loadTeamList()
        await this.loadTeams()
      } catch (e) {
        alert(e?.response?.data?.error || '保存团队失败')
      } finally {
        this.loading = false
      }
    },
    async toggleTeamStatus(team) {
      if (!window.confirm(`确认${team.status === 'ACTIVE' ? '禁用' : '启用'}"${team.name}"吗？`)) return
      const status = team.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
      this.loading = true
      try {
        await therapyTeamService.updateStatus(team.id, status)
        await this.loadTeamList()
        await this.loadTeams()
      } catch (e) {
        alert(e?.response?.data?.error || '更新状态失败')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editingId = null
      this.form = { name: '', gender: '', avatarUrl: '', bio: '', status: 'ACTIVE', teamId: null, notifyWechatUserId: null }
      this.notifyPhone = ''
      this.notifyCandidates = []
      this.notifySearchDone = false
      this.selectedActivityIds = []
      this.therapistActivities = []
      this.previewUrls = {}
      this.showDialog = true
    },
    openEdit(item) {
      this.editingId = item.id
      this.form = {
        name: item.name,
        gender: item.gender || '',
        avatarUrl: item.avatarUrl || '',
        bio: item.bio || '',
        status: item.status,
        teamId: item.team?.id ?? null,
        notifyWechatUserId: item.notifyWechatUserId ?? null
      }
      this.notifyPhone = ''
      this.notifyCandidates = item.notifyWechatUserId
        ? [{ id: item.notifyWechatUserId, nickname: item.notifyWechatNickname, openidHint: '已绑定' }]
        : []
      this.notifySearchDone = false
      const acts = item.activities || []
      this.selectedActivityIds = acts.map(a => a.activityId)
      this.therapistActivities = acts.map(a => ({ activityId: a.activityId, grade: a.grade || '' }))
      this.previewUrls = {}
      this.showDialog = true
    },
    async save() {
      if (!this.form.name) return
      if (!this.form.gender) {
        alert('请选择性别')
        return
      }
      const therapistActivities = this.selectedActivityIds.map(actId => {
        const found = this.therapistActivities.find(a => a.activityId === actId)
        return {
          activityId: actId,
          grade: found ? found.grade : ''
        }
      })
      const payload = {
        name: this.form.name,
        gender: this.form.gender,
        avatarUrl: this.form.avatarUrl ? normalizeOssUrlForPresign(this.form.avatarUrl) : '',
        bio: this.form.bio,
        status: this.form.status,
        teamId: this.form.teamId,
        notifyWechatUserId: this.form.notifyWechatUserId,
        therapistActivities
      }
      this.loading = true
      try {
        if (this.editingId) {
          await therapistService.update(this.editingId, payload)
        } else {
          await therapistService.create(payload)
        }
        this.showDialog = false
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '保存失败')
      } finally {
        this.loading = false
      }
    },
    async searchNotifyCandidates() {
      if (!this.notifyPhone) return
      this.loading = true
      this.notifySearchDone = false
      try {
        const res = await therapistService.fetchNotifyCandidates(this.notifyPhone.trim())
        this.notifyCandidates = res.data || []
        this.notifySearchDone = true
        if (this.notifyCandidates.length === 1) {
          this.form.notifyWechatUserId = this.notifyCandidates[0].id
        }
      } catch (e) {
        alert(e?.response?.data?.error || '搜索失败')
      } finally {
        this.loading = false
      }
    },
    async uploadAvatar(event) {
      const file = event.target.files[0]
      if (!file) return
      this.loadingField = 'avatarUrl'
      try {
        const res = await therapyActivityService.uploadImage(file)
        if (res.success) {
          this.form.avatarUrl = res.data.url
        }
      } catch (e) {
        alert(e?.response?.data?.message || '上传失败')
      } finally {
        this.loadingField = ''
        event.target.value = ''
      }
    },
    getPreviewUrl(url) {
      if (!url) return ''
      if (url.startsWith('data:')) return url
      const cacheKey = normalizeOssUrlForPresign(url)
      if (this.previewUrls[cacheKey]) return this.previewUrls[cacheKey]

      if (url.includes('aliyuncs.com')) {
        therapyActivityService.getPresignedUrl(url).then(res => {
          const signed = res.data?.url || res.url
          if (signed) {
            this.previewUrls = { ...this.previewUrls, [cacheKey]: signed }
          }
        }).catch(err => {
          console.error('获取预签名URL失败:', err)
          this.previewUrls = { ...this.previewUrls, [cacheKey]: url }
        })
        return ''
      }

      if (url.startsWith('http')) return url
      return ''
    },
    async toggleStatus(item) {
      const nextStatus = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
      if (nextStatus === 'INACTIVE') {
        await this.openDeactivateModal(item, nextStatus)
        return
      }
      if (!window.confirm(`确认将理疗师"${item.name}"启用吗？`)) return
      this.loading = true
      try {
        await therapistService.updateStatus(item.id, nextStatus)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '更新状态失败')
      } finally {
        this.loading = false
      }
    },
    async openDeactivateModal(item, status) {
      this.loading = true
      try {
        const res = await therapistService.checkDeactivate(item.id)
        const check = res.data || {}
        this.deactivateTarget = { item, status }
        this.deactivateCheck = check
        this.deactivateModalMode = check.hasActiveBookings ? 'blocked' : 'confirm'
        this.showDeactivateModal = true
      } catch (e) {
        alert(e?.response?.data?.error || '检查预约状态失败')
      } finally {
        this.loading = false
      }
    },
    closeDeactivateModal() {
      this.showDeactivateModal = false
      this.deactivateTarget = null
      this.deactivateCheck = null
    },
    async confirmDeactivate() {
      if (!this.deactivateTarget) return
      const { item, status } = this.deactivateTarget
      this.loading = true
      try {
        await therapistService.updateStatus(item.id, status)
        this.closeDeactivateModal()
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '更新状态失败')
      } finally {
        this.loading = false
      }
    },
    formatBookingStatusBreakdown(breakdown) {
      if (!breakdown) return ''
      return Object.entries(breakdown)
        .map(([status, count]) => `${this.bookingStatusLabels[status] || status} ${count} 笔`)
        .join('、')
    },
    changePage(p) {
      this.page = p
      this.loadData()
    }
  }
}
</script>

<style scoped>
.toolbar select,
.toolbar input {
  height: 40px;
}
.toolbar select {
  min-width: 120px;
}
.bio-textarea {
  width: 100%;
  min-height: 80px;
  resize: vertical;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}
.form-group label {
  font-weight: 500;
  color: #333;
}
.field-label {
  font-weight: 600;
  color: #111827;
  font-size: 0.9375rem;
}
.field-hint {
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
}
.block-hint {
  display: block;
  margin-bottom: 2px;
}
.notify-wechat-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.notify-phone-input {
  flex: 1;
}
.warn-hint {
  color: #b45309;
}
.field-hint strong {
  color: #1e40af;
  font-weight: 600;
}
.form-group label.full-width {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.image-upload-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.avatar-preview-container {
  width: 80px;
  height: 80px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
}
.avatar-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.image-placeholder {
  color: #9ca3af;
  font-size: 0.75rem;
  padding: 0.5rem;
  text-align: center;
}
.image-upload-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.upload-status {
  color: #2563eb;
  font-size: 0.85rem;
  font-weight: 500;
}
.ghost-button {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
}
.danger-link {
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-size: 0.9rem;
}
.therapist-table {
  table-layout: fixed;
}
.activities-cell {
  overflow: hidden;
}
.activities-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-height: 120px;
  overflow-y: auto;
}
.activity-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  color: #8c998a;
  background-color: rgba(140, 153, 138, 0.08);
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
}
.activity-checklist {
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px;
}
.activity-check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  flex-wrap: wrap;
}
.activity-check-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  min-width: 140px;
}
.activity-check-label input[type="checkbox"] {
  margin: 0;
}
.grade-select {
  padding: 2px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.85rem;
  max-width: 140px;
}
.grade-input {
  padding: 2px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.85rem;
  max-width: 120px;
}
.no-activities {
  color: #9ca3af;
  font-size: 0.85rem;
  padding: 8px 0;
}
.team-select {
  width: 100%;
  margin-top: 6px;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}
.dialog-wide {
  max-width: 800px;
  width: 95vw;
}
.category-toolbar {
  margin-bottom: 12px;
}
.category-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}
.category-table th,
.category-table td {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  text-align: left;
  font-size: 0.875rem;
}
.category-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}
.field-block {
  display: block;
  margin-bottom: 12px;
  font-size: 0.875rem;
}
.field-block input {
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
}
.deactivate-summary p {
  margin: 0 0 8px;
  color: #374151;
}
.deactivate-alert {
  border-radius: 8px;
  padding: 14px 16px;
  line-height: 1.6;
}
.deactivate-alert p {
  margin: 0 0 8px;
}
.deactivate-alert p:last-child {
  margin-bottom: 0;
}
.deactivate-alert-danger {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}
.deactivate-alert-warning {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
}
.deactivate-hint {
  font-size: 0.875rem;
  color: #b91c1c;
}
.deactivate-confirm-btn {
  background: #dc2626;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.45rem 1rem;
  cursor: pointer;
}
.deactivate-confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.dialog-cancel-btn {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.45rem 1rem;
  cursor: pointer;
}
</style>